import * as React from "react";
import { useState } from "react";
import { Dialog } from "../../../../bits/Dialog/Dialog";
import { Input } from "../../../../bits/Input/Input";
import { Select } from "../../../../bits/Select/Select";
import { useToasts } from "react-toast-notifications";
import { addLocation } from "../../../../../../store/cache/location";
import { CacheContext } from "../../../../../../contexts/CacheContext";

interface AddNewLocationProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    officeManagersEmails?: string[];
    hasOfficeAdminBetaAccess?: boolean;
}

export default function AddNewLocation({
    open,
    onClose,
    onSuccess,
    officeManagersEmails = [],
    hasOfficeAdminBetaAccess = false,
}: AddNewLocationProps) {
    const { addToast } = useToasts();
    const olc = React.useContext(CacheContext);

    const [newLocation, setNewLocation] = useState("");
    const [newLocationAddress, setNewLocationAddress] = useState("");
    const [selectedOfficeManagers, setSelectedOfficeManagers] = useState<string[]>(
        []
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<{
        location?: string;
        address?: string;
    }>({});

    const handleSubmit = async () => {
        setErrors({});

        const newErrors: typeof errors = {};

        if (!newLocation.trim()) {
            newErrors.location = "Location name is required";
        }

        if (!newLocationAddress.trim()) {
            newErrors.address = "Location address is required";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const locationToAdd = newLocation.trim();
        if (olc?.offices && Object.keys(olc.offices).includes(locationToAdd)) {
            setErrors({ location: "Location already exists" });
            return;
        }

        setIsSubmitting(true);

        try {
            await addLocation(
                locationToAdd,
                newLocationAddress.trim(),
                selectedOfficeManagers.length > 0
                    ? selectedOfficeManagers
                    : undefined
            );

            addToast("New location added", { appearance: "success" });

            setNewLocation("");
            setNewLocationAddress("");
            setSelectedOfficeManagers([]);
            setErrors({});

            onClose();
            onSuccess();
        } catch (ex) {
            console.error(ex);
            addToast("Error saving new location", { appearance: "error" });
        }
    };

    const handleClose = () => {
        setNewLocation("");
        setNewLocationAddress("");
        setSelectedOfficeManagers([]);
        setErrors({});
        onClose();
    };

    // Transform office managers emails to select options
    const officeManagerOptions = officeManagersEmails?.map((manager) => ({
        value: manager,
        label: manager,
    }));

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            title="Add New Location"
            onSave={handleSubmit}
            onCancel={handleClose}
            saveText="Add Location"
            cancelText="Cancel"
            isSubmitBtnDisabled={
                isSubmitting || !newLocation.trim() || !newLocationAddress.trim()
            }
        >
            <div className="v2-form">
                <Input
                    label="Location(Only numbers, alphabets and spaces are allowed)"
                    placeholder="Add new location"
                    value={newLocation}
                    onChange={(e) => setNewLocation(e.target.value)}
                    hasError={!!errors.location}
                    errorText={errors.location}
                    isRequired
                    name="location"
                />

                <Input
                    label="Location Address"
                    placeholder="Add location address"
                    value={newLocationAddress}
                    onChange={(e) => setNewLocationAddress(e.target.value)}
                    hasError={!!errors.address}
                    errorText={errors.address}
                    isRequired
                    name="address"
                />

                {hasOfficeAdminBetaAccess && (
                    <Select
                        label="Office Managers(You can select multiple office managers)"
                        placeholder="Select office managers"
                        options={officeManagerOptions}
                        value={officeManagerOptions.filter((option) =>
                            selectedOfficeManagers.includes(option.value)
                        )}
                        onChange={(selectedOptions) => {
                            if (Array.isArray(selectedOptions)) {
                                setSelectedOfficeManagers(
                                    selectedOptions.map((option) => option.value)
                                );
                            } else {
                                setSelectedOfficeManagers([]);
                            }
                        }}
                        isMulti
                        multiHasValues={selectedOfficeManagers.length > 0}
                        name="officeManagers"
                        isLongListInDialog
                    />
                )}
            </div>
        </Dialog>
    );
}
